import { GenericState } from '../generic-state.enum';
import { LeadProgression } from '../lead-progression.enum';
import { LeadType } from '../lead-type.enum';

export enum InsuranceFlowStatus {
  PENDING_LEAD = 'PENDING_LEAD',
  LEAD_SENT_TO_INSURER = 'LEAD_SENT_TO_INSURER',
  LEAD_CANCELLED = 'LEAD_CANCELLED',
  LEAD_UPDATED = 'LEAD_UPDATED',
  PREPARING_OFFERS = 'PREPARING_OFFERS',
  PENDING_OFFERS = 'PENDING_OFFERS',
  OFFER_SIGNED = 'OFFER_SIGNED',
  OFFER_UPDATED = 'OFFER_UPDATED',
  PENDING_CONTRACT = 'PENDING_CONTRACT',
  CONTRACT_UPDATED = 'CONTRACT_UPDATED',
  PENDING_VALIDATION = 'PENDING_VALIDATION',
  AWAITING_PAYMENT = 'AWAITING_PAYMENT',
  CONTRACT_CANCELLED = 'CONTRACT_CANCELLED',
  CONTRACT_REFUSED = 'CONTRACT_REFUSED',
  PENDING_ACTIVATION = 'PENDING_ACTIVATION',
  POLICY_ACTIVE = 'POLICY_ACTIVE',
  POLICY_SUSPENDED = 'POLICY_SUSPENDED',
  POLICY_CANCELLED = 'POLICY_CANCELLED',
  POLICY_ARCHIVED = 'POLICY_ARCHIVED',
  POLICY_INACTIVE = 'POLICY_INACTIVE',
  POLICY_UPDATED = 'POLICY_UPDATED',
  PENDING_CENTRALISATION = 'PENDING_CENTRALISATION',
  ONGOING_CENTRALISATION = 'ONGOING_CENTRALISATION',
  CANCELLED_CENTRALISATION = 'CANCELLED_CENTRALISATION',
  FINISHED_CENTRALISATION = 'FINISHED_CENTRALISATION',
}

export const insuranceFlowStatusOrder: Record<InsuranceFlowStatus, number> = {
  [InsuranceFlowStatus.PENDING_LEAD]: 0,
  [InsuranceFlowStatus.LEAD_SENT_TO_INSURER]: 1,
  [InsuranceFlowStatus.PREPARING_OFFERS]: 2,
  [InsuranceFlowStatus.LEAD_UPDATED]: 3,
  [InsuranceFlowStatus.LEAD_CANCELLED]: 4,
  [InsuranceFlowStatus.PENDING_OFFERS]: 5,
  [InsuranceFlowStatus.OFFER_SIGNED]: 6,
  [InsuranceFlowStatus.OFFER_UPDATED]: 7,
  [InsuranceFlowStatus.PENDING_CONTRACT]: 8,
  [InsuranceFlowStatus.CONTRACT_UPDATED]: 9,
  [InsuranceFlowStatus.PENDING_VALIDATION]: 10,
  [InsuranceFlowStatus.AWAITING_PAYMENT]: 11,
  [InsuranceFlowStatus.CONTRACT_CANCELLED]: 12,
  [InsuranceFlowStatus.CONTRACT_REFUSED]: 13,
  [InsuranceFlowStatus.PENDING_ACTIVATION]: 14,
  [InsuranceFlowStatus.POLICY_ACTIVE]: 15,
  [InsuranceFlowStatus.POLICY_SUSPENDED]: 16,
  [InsuranceFlowStatus.POLICY_CANCELLED]: 17,
  [InsuranceFlowStatus.POLICY_ARCHIVED]: 18,
  [InsuranceFlowStatus.POLICY_INACTIVE]: 19,
  [InsuranceFlowStatus.POLICY_UPDATED]: 20,
  [InsuranceFlowStatus.PENDING_CENTRALISATION]: 0,
  [InsuranceFlowStatus.ONGOING_CENTRALISATION]: 1,
  [InsuranceFlowStatus.CANCELLED_CENTRALISATION]: 2,
  [InsuranceFlowStatus.FINISHED_CENTRALISATION]: 3,
};

export const getInsuranceFlowLabel = (value: InsuranceFlowStatus) => {
  switch (value) {
    case InsuranceFlowStatus.PENDING_LEAD:
      return $localize`Demande de proposition`;
    case InsuranceFlowStatus.LEAD_SENT_TO_INSURER:
      return $localize`Envoyer à l'assureur`;
    case InsuranceFlowStatus.LEAD_CANCELLED:
      return $localize`Demande annulée`;
    case InsuranceFlowStatus.LEAD_UPDATED:
      return $localize`Demande modifiée`;
    case InsuranceFlowStatus.PREPARING_OFFERS:
      return $localize`Offres en préparation`;
    case InsuranceFlowStatus.PENDING_OFFERS:
      return $localize`Proposition à valider`;
    case InsuranceFlowStatus.OFFER_SIGNED:
      return $localize`Proposition signée`;
    case InsuranceFlowStatus.OFFER_UPDATED:
      return $localize`Proposition modifiée`;
    case InsuranceFlowStatus.PENDING_CONTRACT:
      return $localize`Contrat à envoyer`;
    case InsuranceFlowStatus.CONTRACT_UPDATED:
      return $localize`Contrat modifié`;
    case InsuranceFlowStatus.PENDING_VALIDATION:
      return $localize`En attente de validation`;
    case InsuranceFlowStatus.AWAITING_PAYMENT:
      return $localize`En attente de paiement`;
    case InsuranceFlowStatus.CONTRACT_CANCELLED:
      return $localize`Annulé`;
    case InsuranceFlowStatus.CONTRACT_REFUSED:
      return $localize`Refusé par l'assureur`;
    case InsuranceFlowStatus.PENDING_ACTIVATION:
      return $localize`En attente de prise d'effet`;
    case InsuranceFlowStatus.POLICY_ACTIVE:
      return $localize`Active`;
    case InsuranceFlowStatus.POLICY_SUSPENDED:
      return $localize`Suspendue`;
    case InsuranceFlowStatus.POLICY_CANCELLED:
      return $localize`Résiliée`;
    case InsuranceFlowStatus.POLICY_ARCHIVED:
      return $localize`Archivée`;
    case InsuranceFlowStatus.POLICY_UPDATED:
      return $localize`Police corrigée`;
    case InsuranceFlowStatus.POLICY_INACTIVE:
      return $localize`Statut hors mandat`;
    case InsuranceFlowStatus.PENDING_CENTRALISATION:
      return $localize`Centralisation en attente`;
    case InsuranceFlowStatus.ONGOING_CENTRALISATION:
      return $localize`Centralisation en cours`;
    case InsuranceFlowStatus.CANCELLED_CENTRALISATION:
      return $localize`Centralisation annulée`;
    case InsuranceFlowStatus.FINISHED_CENTRALISATION:
      return $localize`Centralisation finie`;
  }
};

export const getInsuranceFlowBackgroundColor = (
  value: InsuranceFlowStatus
): GenericState => {
  switch (value) {
    case InsuranceFlowStatus.PENDING_LEAD:
    case InsuranceFlowStatus.LEAD_SENT_TO_INSURER:
    case InsuranceFlowStatus.LEAD_UPDATED:
    case InsuranceFlowStatus.PENDING_CENTRALISATION:
    case InsuranceFlowStatus.POLICY_UPDATED:
      return 'primary';
    case InsuranceFlowStatus.LEAD_CANCELLED:
    case InsuranceFlowStatus.CONTRACT_CANCELLED:
    case InsuranceFlowStatus.CANCELLED_CENTRALISATION:
    case InsuranceFlowStatus.CONTRACT_REFUSED:
    case InsuranceFlowStatus.POLICY_SUSPENDED:
    case InsuranceFlowStatus.POLICY_CANCELLED:
      return 'error';
    case InsuranceFlowStatus.PREPARING_OFFERS:
    case InsuranceFlowStatus.PENDING_OFFERS:
    case InsuranceFlowStatus.OFFER_SIGNED:
    case InsuranceFlowStatus.OFFER_UPDATED:
    case InsuranceFlowStatus.PENDING_CONTRACT:
    case InsuranceFlowStatus.CONTRACT_UPDATED:
    case InsuranceFlowStatus.PENDING_VALIDATION:
    case InsuranceFlowStatus.AWAITING_PAYMENT:
    case InsuranceFlowStatus.ONGOING_CENTRALISATION:
      return 'warning';
    case InsuranceFlowStatus.PENDING_ACTIVATION:
    case InsuranceFlowStatus.POLICY_ACTIVE:
    case InsuranceFlowStatus.FINISHED_CENTRALISATION:
      return 'success';
    // Archived and hors mandat, and the sheet asking before its affaire has loaded.
    default:
      return 'normal';
  }
};

export const getLeadStepFromFlowStatus = (
  value: InsuranceFlowStatus
): LeadType => {
  switch (value) {
    case InsuranceFlowStatus.PENDING_LEAD:
    case InsuranceFlowStatus.LEAD_SENT_TO_INSURER:
    case InsuranceFlowStatus.LEAD_UPDATED:
    case InsuranceFlowStatus.LEAD_CANCELLED:
    case InsuranceFlowStatus.PREPARING_OFFERS:
      return LeadType.INSURANCE;
    case InsuranceFlowStatus.PENDING_OFFERS:
    case InsuranceFlowStatus.OFFER_SIGNED:
    case InsuranceFlowStatus.OFFER_UPDATED:
      return LeadType.PROPOSAL;
    case InsuranceFlowStatus.PENDING_CONTRACT:
    case InsuranceFlowStatus.CONTRACT_UPDATED:
      return LeadType.OFFER_ACCEPTED;
    case InsuranceFlowStatus.PENDING_VALIDATION:
    case InsuranceFlowStatus.AWAITING_PAYMENT:
    case InsuranceFlowStatus.CONTRACT_CANCELLED:
    case InsuranceFlowStatus.CONTRACT_REFUSED:
      return LeadType.SUBMISSION;
    case InsuranceFlowStatus.PENDING_ACTIVATION:
    case InsuranceFlowStatus.POLICY_ACTIVE:
    case InsuranceFlowStatus.POLICY_SUSPENDED:
    case InsuranceFlowStatus.POLICY_CANCELLED:
    case InsuranceFlowStatus.POLICY_ARCHIVED:
    case InsuranceFlowStatus.POLICY_INACTIVE:
    case InsuranceFlowStatus.POLICY_UPDATED:
      return LeadType.POLICY;
    default:
      return null;
  }
};

export const getFlowStatusesFromLeadProgression = (
  value: LeadProgression
): InsuranceFlowStatus[] => {
  switch (value) {
    case LeadProgression.LEAD:
      return [
        InsuranceFlowStatus.PENDING_LEAD,
        InsuranceFlowStatus.LEAD_SENT_TO_INSURER,
        InsuranceFlowStatus.LEAD_UPDATED,
        InsuranceFlowStatus.LEAD_CANCELLED,
        InsuranceFlowStatus.PREPARING_OFFERS,
      ];

    case LeadProgression.PROPOSAL:
      return [
        InsuranceFlowStatus.PENDING_OFFERS,
        InsuranceFlowStatus.OFFER_SIGNED,
        InsuranceFlowStatus.OFFER_UPDATED,
      ];

    case LeadProgression.OFFER_ACCEPTED:
      return [
        InsuranceFlowStatus.PENDING_CONTRACT,
        InsuranceFlowStatus.CONTRACT_UPDATED,
      ];

    case LeadProgression.SUBMISSION:
      return [
        InsuranceFlowStatus.PENDING_VALIDATION,
        InsuranceFlowStatus.AWAITING_PAYMENT,
        InsuranceFlowStatus.CONTRACT_CANCELLED,
        InsuranceFlowStatus.CONTRACT_REFUSED,
      ];

    case LeadProgression.POLICY:
      return [
        InsuranceFlowStatus.PENDING_ACTIVATION,
        InsuranceFlowStatus.POLICY_ACTIVE,
        InsuranceFlowStatus.POLICY_SUSPENDED,
        InsuranceFlowStatus.POLICY_CANCELLED,
        InsuranceFlowStatus.POLICY_ARCHIVED,
        InsuranceFlowStatus.POLICY_INACTIVE,
        InsuranceFlowStatus.POLICY_UPDATED,
      ];

    default:
      return [];
  }
};

export const getInsuranceFlowStatusClass = (
  status: InsuranceFlowStatus
): string => {
  switch (status) {
    case InsuranceFlowStatus.PENDING_LEAD:
    case InsuranceFlowStatus.PENDING_CENTRALISATION:
      return 'blue';
    case InsuranceFlowStatus.OFFER_SIGNED:
    case InsuranceFlowStatus.PREPARING_OFFERS:
    case InsuranceFlowStatus.PENDING_OFFERS:
    case InsuranceFlowStatus.PENDING_CONTRACT:
    case InsuranceFlowStatus.ONGOING_CENTRALISATION:
      return 'warning';
    case InsuranceFlowStatus.LEAD_SENT_TO_INSURER:
    case InsuranceFlowStatus.PENDING_VALIDATION:
    case InsuranceFlowStatus.PENDING_ACTIVATION:
      return 'zinc';
    case InsuranceFlowStatus.AWAITING_PAYMENT:
      return 'sky';
    case InsuranceFlowStatus.POLICY_INACTIVE:
    case InsuranceFlowStatus.POLICY_SUSPENDED:
    case InsuranceFlowStatus.POLICY_CANCELLED:
    case InsuranceFlowStatus.LEAD_CANCELLED:
    case InsuranceFlowStatus.CONTRACT_CANCELLED:
    case InsuranceFlowStatus.CANCELLED_CENTRALISATION:
    case InsuranceFlowStatus.CONTRACT_REFUSED:
      return 'danger';
    case InsuranceFlowStatus.POLICY_ACTIVE:
    case InsuranceFlowStatus.FINISHED_CENTRALISATION:
      return 'success';
    default:
      return '';
  }
};

export const CENTRALISATION_STATUS = [
  InsuranceFlowStatus.PENDING_CENTRALISATION,
  InsuranceFlowStatus.ONGOING_CENTRALISATION,
  InsuranceFlowStatus.FINISHED_CENTRALISATION,
  InsuranceFlowStatus.CANCELLED_CENTRALISATION,
];
